import classNames from "classnames";
import React, { ReactNode, useMemo } from "react";
import { toWSDateString, WSDateFormatsType } from "../../../utils";
import { WSElement, WSElementProps } from "../WSElement/WSElement.component";
import { WSAvatar, WSAvatarProps } from "../common/WSAvatar/WSAvatar.component";
import { WSPill, WSPillProps } from "../common/WSPill/WSPill.component";
import { WSText } from "../core/WSText/WSText.component";
import styles from "./WSTimeline.module.scss";

export type WSTimelineProps = {
  items: WSTimelineItemProps[];
  dateFormat?: WSDateFormatsType;
} & WSElementProps;

export type WSTimelineItemProps = {
  date?: Date;
  content: ReactNode;
  pills?: (string | WSPillProps)[];
  avatar?: WSAvatarProps;
  dateFormat?: WSDateFormatsType;
};

export const WSTimeline: React.FC<WSTimelineProps> = ({
  items,
  className,
  dateFormat,
  ...elementProps
}) => {
  return (
    <WSElement
      className={classNames(styles.timeline, className)}
      {...elementProps}
    >
      {items.map((item, index) => (
        <WSTimelineItem key={String(index)} dateFormat={dateFormat} {...item} />
      ))}
    </WSElement>
  );
};

function getAvatarProps(props: WSTimelineItemProps): WSAvatarProps | undefined {
  const baseProps: Partial<WSAvatarProps> = {
    format: "circle",
    size: "S",
    colorBackground: "gray400"
  };

  if (!props.avatar) {
    return {
      ...baseProps,
      type: "icon",
      icon: "info-circle"
    };
  }

  return {
    ...baseProps,
    ...props.avatar
  };
}

const WSTimelineItem: React.FC<WSTimelineItemProps> = (props) => {
  const avatarProps = getAvatarProps(props);

  const pills = useMemo(() => {
    const pills = [...(props.pills || [])];

    if (props.date) {
      pills.push(
        toWSDateString(props.date, props.dateFormat || "monthDayYear")
      );
    }

    return pills;
  }, [props.pills, props.date, props.dateFormat]);

  return (
    <WSElement className={styles.item}>
      {avatarProps && <WSAvatar className={styles.avatar} {...avatarProps} />}

      <WSElement className={styles.card}>
        {pills.length > 0 && (
          <WSElement className={styles.pills}>
            {pills.map((pill) => {
              return (
                <WSElement
                  className={styles.pill}
                  key={typeof pill === "string" ? pill : pill.text}
                >
                  {typeof pill === "string" ? (
                    <WSText.ParagraphSm color="gray400" singleLine>
                      {pill}
                    </WSText.ParagraphSm>
                  ) : (
                    <WSPill {...pill} />
                  )}
                </WSElement>
              );
            })}
          </WSElement>
        )}

        <WSElement className={styles.content}>
          {typeof props.content === "string" ? (
            <WSText.ParagraphSm color="gray600">
              {props.content}
            </WSText.ParagraphSm>
          ) : (
            props.content
          )}
        </WSElement>
      </WSElement>
    </WSElement>
  );
};
